import { useMemo, type ReactElement } from 'react';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSelector } from 'react-redux';

import { FlexColumn } from 'ui-components/components/flex/column';
import { FlexRow, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';

import { FraudIndicatorCard } from './fraud-indicator-card/fraud-indicator-card';
import style from './fraud-indicators-section.module.less';
import { messages } from './messages';
import { BLOCKING_BEHAVIOR } from '../../../constants/fraud-constants';
import { LossPrevention } from '../../../constants/features';
import { useEnableLossPrevention } from '../../../feature-flags/hooks/use-enable-loss-prevention';
import { useFulfillmentPlanIdContext } from '../../../fulfillment-plans/context/fulfillment-plan-id-context-provider';
import { useIsSellerFeatureEnabled } from '../../../hooks/use-is-seller-feature-enabled';
import Global from '../../../store/global-selectors';
import { OrderDetailsSectionId } from '../fulfillment-plan/order-details-expanded-section-provider';
import { useFraudIndicators } from '../hooks/use-fraud-indicators';
import ExpanderSection from '../tab-section/expander-section/expander-section';

const sectionId = OrderDetailsSectionId.FraudAlerts;

export const FraudIndicatorsSection = (): ReactElement | null => {
  const isLossPreventionEnabled = useEnableLossPrevention();
  const isLossPreventionLabsEnabled = useIsSellerFeatureEnabled(LossPrevention);
  const fulfillmentPlanId = useFulfillmentPlanIdContext();
  const fulfillmentPlan = useSelector((state) =>
    fulfillmentPlanId ? Global.selectEntitiesFulfillmentPlans(state)[fulfillmentPlanId] : undefined,
  );
  const { indicators, hasAnyIndicators, hasBlockingIndicators } = useFraudIndicators(fulfillmentPlan);

  const sortedIndicators = useMemo(() => {
    const severityOrder = {
      [BLOCKING_BEHAVIOR.BLOCKS]: 0,
      [BLOCKING_BEHAVIOR.FLAGS]: 1,
      [BLOCKING_BEHAVIOR.INFO]: 2,
    };

    return [...indicators].sort((a, b) => severityOrder[a.blockingBehavior] - severityOrder[b.blockingBehavior]);
  }, [indicators]);

  if (!isLossPreventionEnabled || !isLossPreventionLabsEnabled || !hasAnyIndicators || !fulfillmentPlanId) {
    return null;
  }

  const sectionClassName = hasBlockingIndicators ? style['fraud-section-blocking'] : style['fraud-section-warning'];
  const headerClassName = hasBlockingIndicators ? style['header-blocking'] : style['header-warning'];

  return (
    <ExpanderSection
      ariaLabelMessageDescriptor={messages.sectionAriaLabel}
      className={sectionClassName}
      contentClassName={style['fraud-section-content']}
      headerContent={
        <FlexRow className={headerClassName} gap={Gap.Space8}>
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <Text
            formattedMessageValues={{
              count: indicators.length,
            }}
            textType={TextType.H4}
            value={messages.sectionTitle}
          />
        </FlexRow>
      }
      sectionContent={
        <FlexColumn gap={Gap.Space8}>
          {sortedIndicators.map((indicator) => (
            <FraudIndicatorCard key={indicator.fraudIndicatorCode} fraudIndicator={indicator} fulfillmentPlanId={fulfillmentPlanId} />
          ))}
        </FlexColumn>
      }
      sectionId={sectionId}
    />
  );
};
